/**
 * Dynamic URL Mappings Context
 * Fetches URL mappings from backend and provides routing functions
 * This eliminates the need for hardcoded routes.js
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

const API = process.env.REACT_APP_BACKEND_URL;

const UrlMappingsContext = createContext(null);

// Default mappings used during initial load (before API response)
const DEFAULT_MAPPINGS = {
  home: '',
  park: 'park',
  book: 'book',
  prices: 'prices',
  passes: 'passes',
  faq: 'faq',
  contact: 'contact',
  login: 'login',
  register: 'register',
  account: 'account',
  activities: 'activities',
  classes: 'activities/classes',
  birthdays: 'activities/birthdays',
  physio: 'activities/physio',
  document: 'document',
  blog: 'blog',
  events: 'events',
  forgotPassword: 'forgot-password',
  resetPassword: 'reset-password',
  paymentStatus: 'payment/status',
  'verify-email': 'verify-email',
  'pending-verification': 'pending-verification',
  'activate-pass': 'activate-pass'
};

export const useUrlMappings = () => {
  const context = useContext(UrlMappingsContext);
  if (!context) {
    throw new Error('useUrlMappings must be used within UrlMappingsProvider');
  }
  return context;
};

export const UrlMappingsProvider = ({ children }) => {
  const { i18n } = useTranslation();
  const [languages, setLanguages] = useState([]);
  const [urlMappings, setUrlMappings] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [defaultLang, setDefaultLang] = useState('pl');

  // Fetch all languages with their URL mappings from backend
  const fetchLanguages = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/languages`);
      const langs = response.data || [];
      
      setLanguages(langs);
      
      // Build URL mappings object keyed by language code
      const mappings = {};
      langs.forEach(lang => {
        if (lang.urlMappings && Object.keys(lang.urlMappings).length > 0) {
          mappings[lang.code] = lang.urlMappings;
        } else {
          // Use default mappings if language has none
          mappings[lang.code] = { ...DEFAULT_MAPPINGS };
        }
      });
      
      setUrlMappings(mappings);
      
      // Find default language
      const defaultLanguage = langs.find(l => l.isDefault);
      if (defaultLanguage) {
        setDefaultLang(defaultLanguage.code);
      }
      
      setError(null);
      console.log('[UrlMappings] Loaded languages:', langs.map(l => l.code));
      console.log('[UrlMappings] URL mappings:', mappings);
    } catch (err) {
      console.error('[UrlMappings] Failed to fetch languages:', err);
      setError(err.message);
      // Use default Polish mappings on error
      setUrlMappings({ pl: DEFAULT_MAPPINGS, en: DEFAULT_MAPPINGS });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLanguages();
  }, [fetchLanguages]);

  // Get URL mappings for a specific language
  const getMappings = useCallback((lang) => {
    return urlMappings[lang] || urlMappings[defaultLang] || DEFAULT_MAPPINGS;
  }, [urlMappings, defaultLang]);

  // Check if a language is supported
  const isLanguageSupported = useCallback((lang) => {
    if (!lang || lang.length !== 2) return false;
    // If we have languages loaded, check against them
    if (languages.length > 0) {
      return languages.some(l => l.code === lang);
    }
    // During loading, accept any 2-letter code
    return true;
  }, [languages]);

  // Get localized path for a route key
  const getLocalizedPath = useCallback((key, lang = null) => {
    const currentLang = lang || i18n.language || defaultLang;
    const langPrefix = `/${currentLang}`;
    const mappings = getMappings(currentLang);
    
    // Handle document paths with slugs
    if (key.startsWith('document/')) {
      const slug = key.replace('document/', '');
      return `${langPrefix}/${mappings.document || 'document'}/${slug}`;
    }
    
    const path = mappings[key];
    
    if (path === undefined) {
      // If key not found, return as-is with language prefix
      return `${langPrefix}/${key}`;
    }
    
    return path ? `${langPrefix}/${path}` : langPrefix;
  }, [i18n.language, defaultLang, getMappings]);

  // Get home path for a language
  const getHomePath = useCallback((lang = null) => {
    const currentLang = lang || i18n.language || defaultLang;
    return `/${currentLang}`;
  }, [i18n.language, defaultLang]);

  // Switch language and get new path
  const switchLanguagePath = useCallback((newLang, currentPath) => {
    // Extract current language from path
    const pathParts = currentPath.split('/').filter(Boolean);
    const currentLang = pathParts[0]?.length === 2 ? pathParts[0] : null;
    
    if (!currentLang) {
      return `/${newLang}${currentPath}`;
    }
    
    const pathAfterLang = pathParts.slice(1).join('/');
    const currentMappings = getMappings(currentLang);
    const newMappings = getMappings(newLang);
    
    // Find which route key this path corresponds to
    // Sort by path length (longest first) so 'aktywnosci/zajecia' matches before 'aktywnosci'
    let routeKey = null;
    const sortedEntries = Object.entries(currentMappings)
      .filter(([, value]) => value !== '')
      .sort((a, b) => b[1].length - a[1].length);
    for (const [key, value] of sortedEntries) {
      if (value === pathAfterLang || pathAfterLang.startsWith(value + '/')) {
        routeKey = key;
        break;
      }
    }
    
    if (routeKey && newMappings[routeKey] !== undefined) {
      // Handle paths with additional segments (like document/:slug)
      if (pathAfterLang.includes('/') && currentMappings[routeKey] && !currentMappings[routeKey].includes('/')) {
        const extraPath = pathAfterLang.substring(currentMappings[routeKey].length);
        return `/${newLang}/${newMappings[routeKey]}${extraPath}`;
      }
      return newMappings[routeKey] ? `/${newLang}/${newMappings[routeKey]}` : `/${newLang}`;
    }
    
    // Fallback: just replace language prefix
    return `/${newLang}/${pathAfterLang}`;
  }, [getMappings]);

  // Get all route paths for all languages (for Route generation)
  const getAllRoutePaths = useCallback((routeKey) => {
    const paths = new Set();
    
    Object.entries(urlMappings).forEach(([lang, mappings]) => {
      const path = mappings[routeKey];
      if (path !== undefined && path !== '') {
        paths.add(path);
      }
    });
    
    // Also add default mapping
    if (DEFAULT_MAPPINGS[routeKey]) {
      paths.add(DEFAULT_MAPPINGS[routeKey]);
    }
    
    return Array.from(paths);
  }, [urlMappings]);

  // Get current language from URL path
  const getLangFromPath = useCallback((pathname) => {
    const pathParts = pathname.split('/').filter(Boolean);
    const lang = pathParts[0];
    if (lang?.length === 2 && isLanguageSupported(lang)) {
      return lang;
    }
    return defaultLang;
  }, [isLanguageSupported, defaultLang]);

  // Get all active languages
  const getActiveLanguages = useCallback(() => {
    return languages.filter(l => l.isActive);
  }, [languages]);

  const value = {
    languages,
    urlMappings,
    loading,
    error,
    defaultLang,
    getMappings,
    isLanguageSupported,
    getLocalizedPath,
    getHomePath,
    switchLanguagePath,
    getAllRoutePaths,
    getLangFromPath,
    getActiveLanguages,
    refresh: fetchLanguages
  };

  return (
    <UrlMappingsContext.Provider value={value}>
      {children}
    </UrlMappingsContext.Provider>
  );
};

export default UrlMappingsContext;
